import { TableSeparator } from "./components/TableSeparator";
import { useTableSelection } from "./components/useTableSelection";
import classNames from "classnames";
import { isNumber } from "lodash";
import { TableBody } from "./components/TableBody";
import { TableHeader } from "./components/TableHeader";
import { TableFooter } from "./components/TableFooter";
import { ColumnDefinition } from "./Table.definition";
import styles from "./Table.module.scss";

type TablePropsSelectionOptions<T> = {
  renderActions?: (selectedRows: T[]) => React.ReactElement;
};

type TableProps<T> = {
  data: T[];
  columns: ColumnDefinition<T>[];
  onRowClick?: (row: T, index: number) => void;
  textNoData?: string;
  selectionOptions?: TablePropsSelectionOptions<T>;
  hideHeader?: boolean;
  flatDesign?: boolean;
  noDarkenColor?: boolean;
  ctaNoData?: React.ReactNode;
  className?: string;
  sort?: (key: string) => void;
  footer?: React.ReactNode;
};

export function Table<T>({
  data,
  columns,
  onRowClick,
  textNoData = "Aucune données",
  selectionOptions,
  hideHeader = false,
  flatDesign = false,
  noDarkenColor = false,
  ctaNoData = null,
  className,
  sort,
  footer,
}: TableProps<T>) {
  const selectionState = useTableSelection(!!selectionOptions, data);
  function getTemplateColumns() {
    const template = columns
      .map((column) =>
        isNumber(column.size) ? `${column.size}fr` : column.size
      )
      .join(" ");
    if (selectionState !== null) return `64px ${template}`;
    return template;
  }

  return (
    <div
      className={classNames(
        styles.tablecontainer,
        {
          [styles.flat]: flatDesign,
          [styles.withFooter]: !!footer,
        },
        className
      )}
    >
      <div
        className={styles.table}
        style={{ gridTemplateColumns: getTemplateColumns() }}
      >
        {!hideHeader && (
          <>
            <TableHeader<T>
              columns={columns}
              selectionState={selectionState}
              renderSelectionActions={selectionOptions?.renderActions}
              onSort={sort}
            />
            <TableSeparator
              columns={columns}
              selectable={selectionState !== null}
            />
          </>
        )}
        <TableBody<T>
          columns={columns}
          data={data}
          onRowClick={onRowClick}
          textNoData={textNoData}
          selectionState={selectionState}
          noDarkenColor={noDarkenColor}
        />
        {footer && (
          <>
            <TableSeparator
              columns={columns}
              selectable={selectionState !== null}
            />
            <TableFooter
              nCol={columns.length}
              selectable={selectionState !== null}
            >
              {footer}
            </TableFooter>
          </>
        )}
      </div>
      {!data.length && ctaNoData && (
        <div className={styles.ctaNoData}>{ctaNoData}</div>
      )}
    </div>
  );
}
